"use client";

import React, { useEffect, useState } from "react";
import { MapPin, Mail, Phone, Clock, Send, LoaderCircle, ArrowRight, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
function TimezoneClock() {
  const [time, setTime] = useState<string>("");
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);
  return <div data-section-id="9289" className="flex items-center gap-3 text-primary font-mono text-lg tracking-widest">
            <Clock className="size-5 animate-pulse" />
            <span>{time || "00:00:00"}</span>
            <span className="text-xs text-muted-foreground ml-1">PST</span>
        </div>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20 shadow-[0_0_30px_-5px_var(--color-primary)]">
                    <Send className="size-8 text-primary" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-foreground font-sans">Inquiry Received</h3>
                    <p className="text-muted-foreground max-w-xs mx-auto font-sans">{successMessage}</p>
                </div>
                <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 hover:bg-primary/10 hover:text-primary rounded-lg">
                    New Strategic Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-mono">Principal Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Full Name" {...field} className="bg-background/50 border-primary/20 focus-visible:border-primary focus-visible:ring-primary/20 h-12 rounded-lg font-sans" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-mono">Corporate Email</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="work@company.com" {...field} className="bg-background/50 border-primary/20 focus-visible:border-primary focus-visible:ring-primary/20 h-12 rounded-lg font-sans" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-mono">Contact Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="206-929-1309" {...field} className="bg-background/50 border-primary/20 focus-visible:border-primary focus-visible:ring-primary/20 h-12 rounded-lg font-sans" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-mono">Advisory Interest</FormLabel>
                            <FormControl>
                                <Input placeholder="Technical or Strategic Optimization" {...field} className="bg-background/50 border-primary/20 focus-visible:border-primary focus-visible:ring-primary/20 h-12 rounded-lg font-sans" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-mono">Inquiry Details</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Describe your organizational infrastructure needs or optimization goals..." {...field} className="min-h-[150px] bg-background/50 border-primary/20 focus-visible:border-primary focus-visible:ring-primary/20 resize-none rounded-lg font-sans" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm font-sans">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold tracking-wide bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_rgba(0,255,255,0.3)] transition-all duration-300 rounded-lg font-sans">
                {isSubmitting ? <>
                        <LoaderCircle className="mr-2 size-5 animate-spin" />
                        Processing...
                    </> : <>
                        Submit Inquiry <ArrowRight className="ml-2 size-5" />
                    </>}
            </Button>
        </Form>;
}
export default function ContactTheNight() {
  return <section id="connect-with-advisory" className="dark bg-background text-foreground py-24 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
                <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-[100px]" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    
                    {}
                    <div className="lg:col-span-5 space-y-12 pt-4">
                        <div className="space-y-6">
                            <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Strategic Technical Advisory</p>
                            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight font-sans">
                                Optimize Your <span className="text-primary">Operational</span> Infrastructure
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                                Century Pointe Holdings provides high-level technical oversight and strategic advisory to streamline your digital workflows and internal infrastructures. Connect with our advisors to begin your optimization journey.
                            </p>
                        </div>

                        <Separator className="bg-border/30" />

                        <div className="space-y-8">
                            <div className="flex items-start gap-4 group">
                                <div className="size-10 rounded-lg bg-secondary/50 flex items-center justify-center border border-border group-hover:border-primary/50 transition-colors">
                                    <MapPin className="size-5 text-primary" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-foreground font-sans">Seattle Office</h4>
                                    <p className="text-muted-foreground text-sm mt-1 font-sans">
                                        Seattle, WA 98102<br />
                                        United States
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 group">
                                <div className="size-10 rounded-lg bg-secondary/50 flex items-center justify-center border border-border group-hover:border-primary/50 transition-colors">
                                    <Mail className="size-5 text-primary" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-foreground font-sans">Direct Correspondence</h4>
                                    <p className="text-muted-foreground text-sm mt-1 font-sans">
                                        info@centurypointeholdings.com
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 group">
                                <div className="size-10 rounded-lg bg-secondary/50 flex items-center justify-center border border-border group-hover:border-primary/50 transition-colors">
                                    <Phone className="size-5 text-primary" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-foreground font-sans">Consultancy Line</h4>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="relative flex size-2.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full size-2.5 bg-cyan-500"></span>
                                        </span>
                                        <span className="text-sm text-muted-foreground font-sans">206-929-1309</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 rounded-lg bg-secondary/20 border border-border/30 backdrop-blur-sm">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-mono">Current Advisory Time</p>
                            <TimezoneClock />
                        </div>
                    </div>

                    {}
                    <div className="lg:col-span-7">
                        <Card className="bg-card/50 backdrop-blur-md border-primary/10 shadow-2xl shadow-black/80 overflow-hidden rounded-lg">
                            <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-40" />
                            
                            <CardHeader className="pb-8 border-b border-border/20">
                                <CardTitle className="text-2xl font-bold font-sans">Strategic Inquiry</CardTitle>
                                <CardDescription className="text-base font-sans">
                                    Submit your requirements via our secure channel. Our principal advisors respond to all inquiries within 24 business hours.
                                </CardDescription>
                            </CardHeader>
                            
                            <CardContent className="pt-8">
                                <FormProvider formKey="advisory_connect_form_strategic_inquiries" formId="advisory_connect_form_strategic_inquiries" sectionName="connect-with-advisory" defaultValues={{
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Submit Inquiry" successMessage="Strategic inquiry received. Our advisors will review your request and respond shortly." errorMessage="Inquiry submission failed. Please verify your connection and try again.">
                                    <ContactFormContent />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </section>;
}